import { useSuspenseQuery } from "@apollo/client/react";

import { OrderBy } from "~/common/types";
import type { CompoundRecordComposersBoolExp } from "~/common/types/graphql.generated";
import {
  GetCompoundRecordComposersCountDocument,
  type GetCompoundRecordComposersCountQuery,
  type GetCompoundRecordComposersCountQueryVariables,
  GetCompoundRecordComposersDocument,
  type GetCompoundRecordComposersQuery,
  type GetCompoundRecordComposersQueryVariables,
} from "~/features/compoundRecordComposers/gqls";
import type { CompoundRecordComposerSearchCondition } from "~/features/compoundRecordComposers/types";
import { useTenant } from "~/features/tenant/context";

interface UseComposerListArgs {
  searchConditions: CompoundRecordComposerSearchCondition[];
  pageIndex: number;
  pageSize: number;
}

function buildWhere(conditions: CompoundRecordComposerSearchCondition[]): CompoundRecordComposersBoolExp {
  const clauses: CompoundRecordComposersBoolExp[] = [];

  for (const c of conditions) {
    if (!c.value) continue;
    const ilike = { _ilike: `%${c.value}%` };
    const eq = { _eq: c.value };
    switch (c.field) {
      case "id":
        clauses.push({ id: { _eq: c.value } });
        break;
      case "key":
        clauses.push({ key: c.exactMatch ? eq : ilike });
        break;
      case "recordType":
        clauses.push({ recordType: c.exactMatch ? eq : ilike });
        break;
    }
  }

  return clauses.length > 0 ? { _and: clauses } : {};
}

export function useComposerList({ searchConditions, pageIndex, pageSize }: UseComposerListArgs) {
  const { tenantId } = useTenant();
  if (!tenantId) throw new Error("Tenant ID is required");

  const where = buildWhere(searchConditions);

  const list = useSuspenseQuery<GetCompoundRecordComposersQuery, GetCompoundRecordComposersQueryVariables>(
    GetCompoundRecordComposersDocument,
    {
      variables: {
        tenantId,
        limit: pageSize,
        offset: pageIndex * pageSize,
        where,
        orderBy: [{ updatedAt: OrderBy.Desc }],
      },
    },
  );

  const count = useSuspenseQuery<GetCompoundRecordComposersCountQuery, GetCompoundRecordComposersCountQueryVariables>(
    GetCompoundRecordComposersCountDocument,
    { variables: { tenantId, where } },
  );

  return { list, count };
}
